<script setup>
import { computed, reactive, watch } from 'vue';
import { useStore } from 'vuex';
import HomePage from './components/HomePage.vue';

const store = useStore();

const eventType = computed(() => store.state.home.eventType);
const timestamp = computed(() => store.state.home.timestamp);

const events = computed(() => store.state.events.events);
const apiStillLoading = computed(() => store.state.events.loaded === false);

const featuredEvent = computed(
  () => events.value.find((event) => event.featured),
);

const eventTypes = computed(() => store.getters.links);

const solutions = computed(
  () => store.state.solutions.solutions.map((s) => ({ label: s.name, value: s.id })),
);

const asc = (a, b) => {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
};

const desc = (a, b) => asc(b, a);

const prop = (propName) => (obj) => obj[propName];

const transformSort = (sortF) => (transformF) => (a, b) => sortF(transformF(a), transformF(b));

const transformAsc = transformSort(asc);

const transformDesc = transformSort(desc);

const cleanNameMarkup = (name) => name.replace(/<[^>]*>/, '');

/**
 * Only for Events On-Demand, needs to compare with End Date
 * @param {Object} event The event
 */
const onDemandSort = (event) => {
  const isOnDemand = event.typeEvent.name === 'ON-DEMAND';
  return isOnDemand
    ? cleanNameMarkup(prop('endDateTime')(event))
    : cleanNameMarkup(prop('startDateTime')(event));
};

const nameAsc = transformAsc((x) => cleanNameMarkup(prop('name')(x)));
const nameDesc = transformDesc((x) => cleanNameMarkup(prop('name')(x)));

const onDemandSortAsc = transformAsc((x) => onDemandSort(x));
const onDemandSortDesc = transformDesc((x) => onDemandSort(x));

const orderings = [
  {
    label: 'Order by Later Events',
    value: 'Order by Later Events',
    sortF: onDemandSortDesc,
  },
  {
    label: 'Order by Upcoming Events',
    value: 'Order by Upcoming Events',
    sortF: onDemandSortAsc,
  },
  {
    label: 'Event Name - A to Z',
    value: 'Event Name - A to Z',
    sortF: nameAsc,
  },
  {
    label: 'Event Name - Z to A',
    value: 'Event Name - Z to A',
    sortF: nameDesc,
  },
];

const getDefaultEventTypes = () => (
  eventType.value
    ? eventTypes.value.filter((x) => x.id === eventType.value)
    : eventTypes.value
).map((x) => x.name);

const inputs = reactive({
  eventTypes: getDefaultEventTypes(),
  solutions: [...solutions.value],
  ordering: orderings[1].value,
});

watch(eventTypes, () => {
  inputs.eventTypes = getDefaultEventTypes();
});

watch(solutions, () => {
  inputs.solutions = [...solutions.value];
});

/* eslint-disable */
// event must match at least one event type and one solution type
const filteredEvents = computed(() => {

  const filtered = events.value.filter((e) => {
    const isEventTypeMatch = inputs.eventTypes.some((type) => e?.typeEvent?.name.includes(type))

    const isSolutionMatch = e?.eventTypeSolutions?.some((s) => {
      return inputs.solutions.some((s_) => s_.value === s.id
        // yes this is ugly.
          || (s_.value === 'software-hardcode' && ['Software (Fleet Management)', 'Software (Telematics)'].includes(s.name))
      )
    })

    return isEventTypeMatch && isSolutionMatch;
  })

  return filtered;
});
/* eslint-enable */

const finalizedEvents = computed(() => {
  const { sortF } = orderings.find((o) => o.value === inputs.ordering);
  return [...filteredEvents.value].sort(sortF);
});

const setFilter = (options) => {
  inputs.eventTypes = options.eventTypes;
  inputs.solutions = options.solutions;
  inputs.ordering = options.ordering;
};

const resetFilter = () => {
  inputs.eventTypes = getDefaultEventTypes();
  inputs.solutions = [...solutions.value];
  inputs.ordering = orderings[1].value;
};

watch([eventType, timestamp], resetFilter);
</script>
<template>
  <home-page
    :eventTypes="eventTypes"
    :orderings="orderings"
    :solutions="solutions"
    :inputs="inputs"
    :events="finalizedEvents"
    :featuredEvent="featuredEvent"
    :apiStillLoading="apiStillLoading"
    @filter="setFilter"
    @reset="resetFilter"
    >
  </home-page>
</template>
